import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { MessageSquare, Plus, Filter } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Community = () => {
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('General');

  useEffect(() => {
    fetchDiscussions();
  }, [selectedCategory]);

  const fetchDiscussions = async () => {
    try {
      let url = `${API}/discussions`;
      if (selectedCategory !== 'All') {
        url += `?category=${selectedCategory}`;
      }
      const response = await axios.get(url);
      setDiscussions(response.data);
    } catch (error) {
      console.error('Failed to fetch discussions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDiscussion = async () => {
    if (!title || !content) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await axios.post(
        `${API}/discussions?title=${encodeURIComponent(title)}&content=${encodeURIComponent(content)}&category=${category}`,
        {},
        { withCredentials: true }
      );
      toast.success('Discussion created successfully!');
      setShowCreateModal(false);
      setTitle('');
      setContent('');
      fetchDiscussions();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to create a discussion');
        const redirectUrl = `${window.location.origin}/auth-callback`;
        window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
      } else {
        toast.error('Failed to create discussion');
      }
    }
  };

  return (
    <div className="min-h-screen bg-black" data-testid="community-page">
      <Navbar />
      
      <div className="pt-24 px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="community-title">
                Collector <span className="accent-red">Community</span>
              </h1>
              <p className="text-gray-400" data-testid="community-description">
                Connect, discuss, and share your passion with fellow collectors
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary flex items-center space-x-2"
              data-testid="create-discussion-btn"
            >
              <Plus size={20} />
              <span>New Discussion</span>
            </button>
          </div>

          {/* Filters */}
          <div className="mb-8 flex items-center space-x-4">
            <Filter size={20} className="text-red-500" />
            <div className="flex flex-wrap gap-2">
              {['All', 'General', 'Marvel', 'DC', 'Anime'].map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-lg font-semibold transition ${
                    selectedCategory === cat
                      ? 'bg-red-600 text-white'
                      : 'bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}
                  data-testid={`filter-${cat.toLowerCase()}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Discussions */}
          {loading ? (
            <div className="text-center py-20" data-testid="loading">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600 mx-auto mb-4"></div>
              <p className="text-gray-400">Loading discussions...</p>
            </div>
          ) : discussions.length > 0 ? (
            <div className="space-y-4" data-testid="discussions-list">
              {discussions.map(discussion => (
                <Link
                  key={discussion.id}
                  to={`/discussion/${discussion.id}`}
                  className="glass-effect p-6 rounded-xl hover:bg-white/10 transition block"
                  data-testid={`discussion-${discussion.id}`}
                >
                  <div className="flex items-start space-x-4">
                    {discussion.user?.picture ? (
                      <img src={discussion.user.picture} alt={discussion.user.name} className="w-12 h-12 rounded-full" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center font-bold">
                        {discussion.user?.name?.charAt(0) || 'A'}
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xl font-semibold">{discussion.title}</h3>
                        <span className="text-sm text-red-500 font-semibold">{discussion.category}</span>
                      </div>
                      <p className="text-gray-400 mb-3 line-clamp-2">{discussion.content}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>{discussion.user?.name || 'Anonymous'}</span>
                        <span>•</span>
                        <span>{new Date(discussion.created_at).toLocaleDateString()}</span>
                        <span>•</span>
                        <div className="flex items-center space-x-1">
                          <MessageSquare size={16} />
                          <span>{discussion.replies?.length || 0} replies</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20" data-testid="no-discussions">
              <MessageSquare className="mx-auto mb-6 text-gray-600" size={80} />
              <h2 className="text-2xl font-bold mb-4">No discussions yet</h2>
              <p className="text-gray-400 mb-8">Be the first to start a conversation!</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary"
              >
                Create Discussion
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create Discussion Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)} data-testid="create-modal">
          <div className="bg-gray-900 rounded-2xl p-8 max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-3xl font-bold mb-6" data-testid="modal-title">Start a Discussion</h2>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-semibold mb-2">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
                  data-testid="category-select"
                >
                  <option value="General">General</option>
                  <option value="Marvel">Marvel</option>
                  <option value="DC">DC Comics</option>
                  <option value="Anime">Anime</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="What's on your mind?"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
                  data-testid="title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Content</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Share your thoughts..."
                  rows="6"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500 resize-none"
                  data-testid="content-input"
                />
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 btn-secondary"
                data-testid="modal-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateDiscussion}
                className="flex-1 btn-primary"
                data-testid="modal-submit-btn"
              >
                Post Discussion
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Community;